/**
 * Toggle and Checkbox Components
 *
 * Accessible toggle switches and checkboxes following WAI-ARIA patterns.
 *
 * Usage:
 * <Toggle checked={value} onChange={setValue} label="Enable feature" />
 * <Checkbox checked={value} onChange={setValue} label="I agree" />
 */

import React, { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';

// ========================================
// Toggle Switch
// ========================================

const Toggle = forwardRef(({
  checked = false,
  onChange,
  label,
  description,
  size = 'md',
  disabled = false,
  error,
  className = '',
  id: providedId,
  name,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const sizeStyles = {
    sm: {
      track: 'w-8 h-4',
      thumb: 'w-3 h-3',
      translate: 'translate-x-4',
      label: 'text-sm',
    },
    md: {
      track: 'w-10 h-5',
      thumb: 'w-4 h-4',
      translate: 'translate-x-5',
      label: 'text-sm',
    },
    lg: {
      track: 'w-12 h-6',
      thumb: 'w-5 h-5',
      translate: 'translate-x-6',
      label: 'text-base',
    },
  };

  const sizes = sizeStyles[size];

  const handleChange = (e) => {
    if (!disabled) {
      onChange?.(e.target.checked, e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!disabled) {
        onChange?.(!checked);
      }
    }
  };

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <button
        ref={ref}
        type="button"
        role="switch"
        id={id}
        name={name}
        aria-checked={checked}
        aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        onKeyDown={handleKeyDown}
        className={`
          relative inline-flex flex-shrink-0 ${sizes.track}
          rounded-full transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-portfolio-purple focus:ring-offset-2
          focus:ring-offset-gray-900
          ${checked
            ? 'bg-portfolio-purple'
            : 'bg-white/20'
          }
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer'
          }
          ${error ? 'ring-2 ring-error-500' : ''}
        `}
        {...props}
      >
        <span
          className={`
            ${sizes.thumb}
            pointer-events-none inline-block rounded-full bg-white shadow-lg
            transform ring-0 transition-transform duration-200 ease-in-out
            ${checked ? sizes.translate : 'translate-x-0.5'}
          `}
          style={{ marginTop: '0.125rem' }}
        />
      </button>

      {(label || description) && (
        <div className="flex-1">
          {label && (
            <label
              htmlFor={id}
              className={`
                block font-medium text-white
                ${sizes.label}
                ${disabled ? 'opacity-50' : 'cursor-pointer'}
              `}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              id={descriptionId}
              className={`mt-0.5 text-gray-400 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
            >
              {description}
            </p>
          )}
          {error && (
            <p id={errorId} className="mt-1 text-sm text-error-400">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Toggle.displayName = 'Toggle';

Toggle.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.node,
  description: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

// ========================================
// Checkbox
// ========================================

export const Checkbox = forwardRef(({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  description,
  size = 'md',
  disabled = false,
  error,
  className = '',
  id: providedId,
  name,
  value,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const sizeStyles = {
    sm: {
      box: 'w-4 h-4',
      icon: 'w-2.5 h-2.5',
      label: 'text-sm',
    },
    md: {
      box: 'w-5 h-5',
      icon: 'w-3 h-3',
      label: 'text-sm',
    },
    lg: {
      box: 'w-6 h-6',
      icon: 'w-4 h-4',
      label: 'text-base',
    },
  };

  const sizes = sizeStyles[size];

  const handleChange = (e) => {
    if (!disabled) {
      onChange?.(e.target.checked, e);
    }
  };

  // Set indeterminate state via ref
  const checkboxRef = React.useCallback((node) => {
    if (node) {
      node.indeterminate = indeterminate;
    }
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [indeterminate, ref]);

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className="relative flex items-center">
        <input
          ref={checkboxRef}
          type="checkbox"
          id={id}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
          aria-invalid={!!error}
          className={`
            ${sizes.box}
            appearance-none rounded border-2
            transition-colors duration-150
            focus:outline-none focus:ring-2 focus:ring-portfolio-purple focus:ring-offset-2
            focus:ring-offset-gray-900
            ${checked || indeterminate
              ? 'bg-portfolio-purple border-portfolio-purple'
              : 'bg-transparent border-white/30 hover:border-white/50'
            }
            ${disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
            }
            ${error ? 'border-error-500' : ''}
          `}
          {...props}
        />
        {/* Checkmark or indeterminate icon */}
        <span
          className={`
            absolute inset-0 flex items-center justify-center
            pointer-events-none
            ${checked || indeterminate ? 'opacity-100' : 'opacity-0'}
            transition-opacity duration-150
          `}
        >
          {indeterminate ? (
            <svg className={`${sizes.icon} text-white`} fill="currentColor" viewBox="0 0 12 12">
              <rect x="2" y="5" width="8" height="2" rx="1" />
            </svg>
          ) : (
            <svg className={`${sizes.icon} text-white`} fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="2">
              <path d="M2 6l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </div>

      {(label || description) && (
        <div className="flex-1">
          {label && (
            <label
              htmlFor={id}
              className={`
                block font-medium text-white
                ${sizes.label}
                ${disabled ? 'opacity-50' : 'cursor-pointer'}
              `}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              id={descriptionId}
              className={`mt-0.5 text-gray-400 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
            >
              {description}
            </p>
          )}
          {error && (
            <p id={errorId} className="mt-1 text-sm text-error-400">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  checked: PropTypes.bool,
  indeterminate: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.node,
  description: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
};

// ========================================
// Radio Button
// ========================================

export const Radio = forwardRef(({
  checked = false,
  onChange,
  label,
  description,
  size = 'md',
  disabled = false,
  error,
  className = '',
  id: providedId,
  name,
  value,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const sizeStyles = {
    sm: {
      box: 'w-4 h-4',
      dot: 'w-1.5 h-1.5',
      label: 'text-sm',
    },
    md: {
      box: 'w-5 h-5',
      dot: 'w-2 h-2',
      label: 'text-sm',
    },
    lg: {
      box: 'w-6 h-6',
      dot: 'w-2.5 h-2.5',
      label: 'text-base',
    },
  };

  const sizes = sizeStyles[size];

  const handleChange = (e) => {
    if (!disabled) {
      onChange?.(e.target.value, e);
    }
  };

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className="relative flex items-center">
        <input
          ref={ref}
          type="radio"
          id={id}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={handleChange}
          aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
          aria-invalid={!!error}
          className={`
            ${sizes.box}
            appearance-none rounded-full border-2
            transition-colors duration-150
            focus:outline-none focus:ring-2 focus:ring-portfolio-purple focus:ring-offset-2
            focus:ring-offset-gray-900
            ${checked
              ? 'bg-portfolio-purple border-portfolio-purple'
              : 'bg-transparent border-white/30 hover:border-white/50'
            }
            ${disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
            }
            ${error ? 'border-error-500' : ''}
          `}
          {...props}
        />
        {/* Inner dot */}
        <span
          className={`
            absolute inset-0 flex items-center justify-center
            pointer-events-none
          `}
        >
          <span
            className={`
              ${sizes.dot} bg-white rounded-full
              transition-transform duration-150
              ${checked ? 'scale-100' : 'scale-0'}
            `}
          />
        </span>
      </div>

      {(label || description) && (
        <div className="flex-1">
          {label && (
            <label
              htmlFor={id}
              className={`
                block font-medium text-white
                ${sizes.label}
                ${disabled ? 'opacity-50' : 'cursor-pointer'}
              `}
            >
              {label}
            </label>
          )}
          {description && (
            <p
              id={descriptionId}
              className={`mt-0.5 text-gray-400 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
            >
              {description}
            </p>
          )}
          {error && (
            <p id={errorId} className="mt-1 text-sm text-error-400">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Radio.displayName = 'Radio';

Radio.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.node,
  description: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
};

// ========================================
// Radio Group
// ========================================

export const RadioGroup = ({
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  size = 'md',
  disabled = false,
  error,
  className = '',
}) => {
  return (
    <div
      role="radiogroup"
      aria-orientation={orientation}
      className={`
        ${orientation === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-3'}
        ${className}
      `}
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={onChange}
          label={option.label}
          description={option.description}
          disabled={disabled || option.disabled}
          size={size}
        />
      ))}
      {error && (
        <p className="mt-1 text-sm text-error-400">{error}</p>
      )}
    </div>
  );
};

RadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    description: PropTypes.node,
    disabled: PropTypes.bool,
  })).isRequired,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
};

// ========================================
// Checkbox Group
// ========================================

export const CheckboxGroup = ({
  name,
  value = [],
  onChange,
  options,
  orientation = 'vertical',
  size = 'md',
  disabled = false,
  error,
  className = '',
}) => {
  const handleChange = (checked, optionValue) => {
    if (checked) {
      onChange?.([...value, optionValue]);
    } else {
      onChange?.(value.filter(v => v !== optionValue));
    }
  };

  return (
    <div
      role="group"
      className={`
        ${orientation === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-3'}
        ${className}
      `}
    >
      {options.map((option) => (
        <Checkbox
          key={option.value}
          name={name}
          value={option.value}
          checked={value.includes(option.value)}
          onChange={(checked) => handleChange(checked, option.value)}
          label={option.label}
          description={option.description}
          disabled={disabled || option.disabled}
          size={size}
        />
      ))}
      {error && (
        <p className="mt-1 text-sm text-error-400">{error}</p>
      )}
    </div>
  );
};

CheckboxGroup.propTypes = {
  name: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    description: PropTypes.node,
    disabled: PropTypes.bool,
  })).isRequired,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
};

export default Toggle;
